import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setModalId } from '../../../store/slices/publicSlice';
import InfoText from '../../atoms/InfoText';
import TitleText from '../../atoms/TitleText';
import { SellerProductBasicDetails } from './types';
import StockStatusBadge from '../../atoms/StockStatusBadge';
import PreviewFile from '../PreviewFile';
import type { CategoryAttribute } from './types';

type Props = {
  basicDetails?: SellerProductBasicDetails;
  categoryAttributes?: CategoryAttribute[];
};

export default function ProductInfoSection({
  basicDetails,
  categoryAttributes,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const dispatch = useDispatch();

  // Extract all used attributes from variants
  const allUsedAttributes =
    basicDetails?.variants?.flatMap((v) => v.attributes) || [];

  // Identify which category attributes are treated as colors (they have images)
  const colorAttributeIds = new Set(
    allUsedAttributes
      .filter((a) => a.colorImageUrl)
      .map((a) => a.categoryAttributeId)
  );

  // Get unique color items across all variants
  const uniqueColorAttributes = Array.from(
    new Map(
      allUsedAttributes
        .filter((a) => a.colorImageUrl)
        .map((a) => [a.categoryAttributeDetailName || a.nameEn || a.nameAr, a])
    ).values()
  );

  const colorsList = uniqueColorAttributes.map((a) => ({
    name:
      a.categoryAttributeDetailName ||
      (locale === 'ar' ? a.nameAr : a.nameEn) ||
      a.nameEn ||
      a.nameAr ||
      '',
    colorImageUrl: a.colorImageUrl || '',
  }));

  // Group other attributes (Material, Size, etc.)
  const otherAttributesGroupedMap = new Map<
    string,
    { title: string; values: Set<string> }
  >();

  allUsedAttributes
    .filter((a) => !colorAttributeIds.has(a.categoryAttributeId))
    .forEach((a) => {
      let groupTitle = a.categoryAttributeName;
      let value = a.categoryAttributeDetailName;

      // Fallback to categoryAttributes if categoryAttributeName is missing
      if (!groupTitle && categoryAttributes) {
        const ca = categoryAttributes.find(
          (ca) => ca.id === a.categoryAttributeId
        );
        if (ca) {
          groupTitle =
            (locale === 'ar' ? ca.nameAr : ca.nameEn) || ca.nameEn || ca.nameAr;
        }
      }

      // Fallback for value
      if (!value) {
        value = (locale === 'ar' ? a.nameAr : a.nameEn) || a.nameEn || a.nameAr;
      }

      if (groupTitle && value) {
        if (!otherAttributesGroupedMap.has(groupTitle)) {
          otherAttributesGroupedMap.set(groupTitle, {
            title: groupTitle,
            values: new Set(),
          });
        }
        otherAttributesGroupedMap.get(groupTitle)!.values.add(value);
      }
    });

  const otherAttributesGrouped = Array.from(
    otherAttributesGroupedMap.values()
  ).map((g) => ({
    title: g.title,
    values: Array.from(g.values),
  }));

  const hasVariations =
    colorsList.length > 0 || otherAttributesGrouped.length > 0;

  return (
    <div
      className="flex flex-col gap-6 pb-4 border-b border-[#E7E7E9] dark:border-gray-800"
      data-testid="seller-product-info-section"
    >
      <div
        className="flex flex-col gap-4"
        data-testid="seller-product-info-details"
      >
        {/* Product Category */}
        <div
          className="flex flex-col gap-2"
          data-testid="seller-product-categories-wrapper"
        >
          <div
            className="flex items-center gap-2 flex-wrap"
            data-testid="seller-product-categories"
          >
            {basicDetails?.category?.name && (
              <div
                className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 !text-base !font-normal text-[#111928] dark:text-gray-300"
                data-testid="seller-product-category"
              >
                {basicDetails.category.name}
              </div>
            )}
            {basicDetails?.speciality?.name && (
              <div
                className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 !text-base !font-normal text-[#111928] dark:text-gray-300"
                data-testid="seller-product-speciality"
              >
                {basicDetails.speciality.name}
              </div>
            )}
          </div>
        </div>

        {/* Product Name & Status */}
        <div
          className="flex flex-row items-center gap-4"
          data-testid="seller-product-title-wrapper"
        >
          <div
            className="flex flex-col gap-1"
            data-testid="seller-product-title-inner"
          >
            <InfoText
              text={t('components.productDetailsPage.productName')}
              className="!text-[#777777] !text-sm"
            />
            <TitleText
              text={basicDetails?.productName || ''}
              size="lg"
              className="!font-bold !text-[#090909] dark:!text-white"
              data-testid="seller-product-info-name"
            />
          </div>

          <StockStatusBadge
            code={basicDetails?.stockStatus?.code}
            name={basicDetails?.stockStatus?.name}
          />
        </div>

        {/* Description */}
        <div
          className="flex flex-col gap-1 pb-4 border-b border-[#E7E7E9] dark:border-gray-800"
          data-testid="seller-product-desc-wrapper"
        >
          <InfoText
            text={t('components.productDetailsPage.description')}
            className="!text-[#777777] !text-sm"
          />
          <InfoText
            text={basicDetails?.description || ''}
            maxLength="FULL"
            className="!text-[#090909] dark:!text-gray-300 leading-relaxed text-sm"
            data-testid="seller-product-info-description"
          />
        </div>

        {/* Variations Section */}
        {hasVariations && (
          <div
            className="flex flex-col gap-6 mt-2"
            data-testid="seller-product-info-variations"
          >
            <InfoText
              text={t('components.productDetailsPage.variations')}
              className="!text-gray-400 !text-sm"
            />

            {/* Colors */}
            {colorsList.length > 0 && (
              <div
                className="flex flex-col gap-3"
                data-testid="seller-product-colors-section"
              >
                <InfoText
                  text={t('components.productDetailsPage.colors')}
                  className="!font-semibold !text-sm !text-[#090909] dark:!text-white"
                />
                <div
                  className="flex gap-4 flex-wrap"
                  data-testid="seller-product-colors-list"
                >
                  {colorsList.map((attr, idx) => (
                    <div
                      key={idx}
                      className="flex flex-col items-center gap-2"
                      data-testid={`seller-product-color-${idx}`}
                    >
                      <div
                        className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 relative bg-gray-50 dark:bg-gray-800 cursor-pointer"
                        data-testid={`seller-product-color-img-wrapper-${idx}`}
                        onClick={() =>
                          dispatch(
                            setModalId({
                              id: 'VIEW_FILE',
                              extraData: {
                                url: attr.colorImageUrl,
                                type: 'IMAGE',
                              },
                            })
                          )
                        }
                      >
                        <img
                          src={attr.colorImageUrl!}
                          alt={attr.name || 'Color'}
                          className="w-full h-full object-cover"
                          data-testid={`seller-product-color-img-${idx}`}
                        />
                      </div>
                      <TitleText
                        text={attr.name || ''}
                        size="sm"
                        className="!text-[#090909] dark:!text-gray-300 !font-normal"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Other Attributes (Material, Size, etc.) */}
            {otherAttributesGrouped.map((group, idx) => (
              <div
                key={idx}
                className="flex flex-col gap-3"
                data-testid={`seller-product-attr-group-${idx}`}
              >
                <TitleText
                  text={group.title}
                  className="!font-semibold !text-sm !text-[#090909] dark:!text-white"
                />
                <div
                  className="flex gap-3 flex-wrap"
                  data-testid={`seller-product-attr-pills-${idx}`}
                >
                  {group.values.map((val, vIdx) => (
                    <div
                      key={vIdx}
                      className="px-4 py-2 border border-[#E7E7E9] dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
                      data-testid={`seller-product-attr-pill-${idx}-${vIdx}`}
                    >
                      <InfoText
                        text={val}
                        className="!text-sm !font-normal !m-0 !text-[#111928] dark:!text-gray-300"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PreviewFile />
    </div>
  );
}
